import React, { useEffect, useMemo, useState } from 'react';
import { ArrowsOut, Pause, Play, X } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { resolveMediaUrl } from '../utils/media';
import { Button } from '../components/ui/button';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function PhotoWallProjectorPage() {
  const { currentEvent } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const wall = currentEvent?.guest_experience?.photo_wall;
  const token = wall?.sources?.find((source) => source.enabled !== false)?.token || wall?.public_token;

  const refresh = async () => {
    if (!currentEvent?.id) return;
    const response = await api.get(`/events/${currentEvent.id}/photo-wall/feed`, { params: { limit: 200 } });
    setPhotos(response.data || []);
  };

  useEffect(() => {
    refresh().catch(() => {});
    const timer = window.setInterval(() => refresh().catch(() => {}), 12000);
    return () => window.clearInterval(timer);
  }, [currentEvent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token || !window.EventSource) return undefined;
    const source = new window.EventSource(`${BACKEND_URL}/api/photo-wall/${token}/stream`);
    source.addEventListener('photos', (message) => {
      try {
        setPhotos(JSON.parse(message.data));
      } catch (_error) {
        // Polling remains as a fallback.
      }
    });
    source.onerror = () => source.close();
    return () => source.close();
  }, [token]);

  useEffect(() => {
    if (paused || photos.length <= 1) return undefined;
    const timer = window.setTimeout(() => setActiveIndex((current) => (current + 1) % photos.length), 6000);
    return () => window.clearTimeout(timer);
  }, [activeIndex, paused, photos.length]);

  useEffect(() => {
    if (activeIndex >= photos.length) setActiveIndex(0);
  }, [activeIndex, photos.length]);

  const active = photos[activeIndex];
  const names = useMemo(
    () => (
      currentEvent?.event_type === 'wedding'
        ? [currentEvent?.bride_name, currentEvent?.groom_name].filter(Boolean).join(' & ')
        : currentEvent?.event_type === 'birthday'
          ? (currentEvent?.honoree_names || []).join(' & ')
        : (currentEvent?.host_names || []).join(' & ')
    ) || currentEvent?.name,
    [currentEvent]
  );

  const enterFullscreen = () => document.documentElement.requestFullscreen?.();

  if (wall?.projector_enabled === false) {
    return <div className="flex min-h-screen items-center justify-center bg-black px-6 text-center text-white">The venue projector is disabled for this event.</div>;
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-black text-white">
      {active ? (
        <>
          <img src={resolveMediaUrl(active.url, { preferProxy: true })} alt="Live guest moment" className="absolute inset-0 h-full w-full object-cover opacity-35 blur-3xl scale-110" />
          <img key={active.id} src={resolveMediaUrl(active.url, { preferProxy: true })} alt="Live guest moment" className="relative z-10 max-h-screen max-w-full animate-in fade-in-0 zoom-in-95 duration-1000 object-contain" />
        </>
      ) : (
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-white/50">Live Photo Wall</p>
          <h1 className="mt-5 font-serif text-5xl">{names}</h1>
          <p className="mt-4 text-white/55">Waiting for the first automatically approved guest photo…</p>
        </div>
      )}

      <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent p-5">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-white/65">Live Photo Wall</p>
          <p className="mt-1 font-serif text-2xl">{names}</p>
        </div>
        <div className="flex gap-2">
          <Button size="icon" variant="secondary" onClick={() => setPaused((value) => !value)} className="rounded-full bg-white/15 text-white hover:bg-white/25">
            {paused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
          </Button>
          <Button size="icon" variant="secondary" onClick={enterFullscreen} className="rounded-full bg-white/15 text-white hover:bg-white/25"><ArrowsOut className="h-5 w-5" /></Button>
          <Button asChild size="icon" variant="secondary" className="rounded-full bg-white/15 text-white hover:bg-white/25"><Link to="/dashboard/photos"><X className="h-5 w-5" /></Link></Button>
        </div>
      </div>

      {active && (
        <div className="absolute bottom-5 left-1/2 z-20 -translate-x-1/2 rounded-full bg-black/45 px-4 py-2 text-sm backdrop-blur">
          {active.location_label || 'Guest moment'} · {active.like_count || 0} favorites
        </div>
      )}
    </main>
  );
}
